import { ErrorHandler } from '@src/components/ErrorHandler';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { usePagination } from '@uhg-abyss/web/hooks/usePagination';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { motion } from 'framer-motion';
import { isEqual, isNil, isNull, isString, isUndefined, merge } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import tableConfiguration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table
 *
 * Reusable table component to be used/reused throughout various screens in the UI.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function Table(props) {
  const {
    columns,
    configuration,
    error: errorObject,
    footer,
    footerBottom,
    footerLeft,
    footerRight,
    footerTop,
    header,
    headerBottom,
    headerLeft,
    headerRight,
    headerTop,
    isLoading,
    noDataMessage,
    requestArgs,
    requestFunction,
    requestKey,
    rows,
    totalPages,
    totalRecords,
  } = props;
  const [initialRequest, setInitialRequest] = useState(false);
  const [previousPayload, setPreviousPayload] = useState({});

  const [resultsPerPage, setResultsPerPage] = useState(
    configuration?.pageSizeDefault || tableConfiguration?.pageSizeDefault
  );

  const [sortBy, setSortBy] = useState(requestArgs?.sort);

  const paginationProps = usePagination({ pages: totalPages, pageSize: resultsPerPage, start: requestArgs?.page });

  const currentPage = paginationProps?.pageIndex;

  /**
   * Defines the final table configuration to pass to abyss useDataTable hook.
   */
  const theConfiguration = useMemo(() => {
    const mergedConfiguration = merge(
      {},
      merge({}, { ...tableConfiguration, ...{ renderSubComponent: null } }, configuration),
      {
        errorMessage: !isNil(errorObject) && (
          <Alert
            css={{
              marginLeft: 'var(--abyss-space-md)',
              marginRight: 'var(--abyss-space-md)',
            }}
            errorCode={errorObject?.error?.statusCode}
            title={`API Error - ${errorObject?.error?.statusText}`}
            variant="error"
          />
        ),
        initialColumns: columns || [],
        noDataMessage:
          isNull(noDataMessage) || isString(noDataMessage) ? (
            <Alert
              css={{
                marginLeft: 'var(--abyss-space-md)',
                marginRight: 'var(--abyss-space-md)',
              }}
              title={noDataMessage || 'No records found.'}
              variant="info"
            />
          ) : (
            noDataMessage
          ),
        uniqueStorageId: `table-${requestKey}`,
      }
    );

    if (!isUndefined(configuration?.pageSizeOptions)) {
      mergedConfiguration.pageSizeOptions = configuration?.pageSizeOptions;
    }

    if (!isUndefined(configuration?.pageSizeDefault)) {
      mergedConfiguration.pageSizeDefault = configuration?.pageSizeDefault;
    }

    return mergedConfiguration;
  }, [tableConfiguration, configuration, columns, errorObject, noDataMessage, requestKey]);

  /**
   * Table configuration.
   */
  const tableState = useDataTable(theConfiguration);

  tableState.state.isLoading = !!isLoading;

  const handleRequest = async (payload) => {
    if (!isEqual(payload, previousPayload)) {
      setPreviousPayload(payload);
      await requestFunction(payload);
    }
  };

  /**
   * Triggers the initial data fetch request.
   */
  useEffect(() => {
    if (!initialRequest && !isNil(currentPage) && !isNil(resultsPerPage) && !isNil(sortBy)) {
      (async () => {
        await handleRequest({
          ...requestArgs,
          ...{
            page: currentPage,
            size: resultsPerPage,
            sort: sortBy,
          },
        });
        setInitialRequest(true);
      })();
    }
  }, [initialRequest, currentPage, resultsPerPage, sortBy]);

  /**
   * When a table column is clicked, update the state with the column name to sort by and the sorting direction.
   */
  useEffect(() => {
    if (!isNil(tableState?.state?.sortBy?.[0]?.id) && !isNil(tableState?.state?.sortBy?.[0]?.desc)) {
      setSortBy(
        `${tableState?.state?.sortBy?.[0]?.id},${tableState?.state?.sortBy?.[0]?.desc === true ? 'desc' : 'asc'}`
      );
    }
  }, [tableState?.state?.sortBy]);

  /**
   * When any of the request arguments change, fetch new data matching the requested arguments.
   */
  useEffect(() => {
    if (initialRequest && !isNil(currentPage) && !isNil(resultsPerPage) && !isNil(sortBy)) {
      (async () => {
        await handleRequest({
          ...requestArgs,
          ...{
            page: currentPage,
            size: resultsPerPage,
            sort: sortBy,
          },
        });
      })();
    }
  }, [initialRequest, currentPage, resultsPerPage, sortBy, requestArgs]);

  /**
   * Data is passed into this component via the rows prop. Update the data table with the newly fetched data.
   */
  useEffect(() => {
    if (tableState?.rows !== rows) {
      tableState?.setColumns(columns, true);
      tableState?.setData(rows, true);
    }
  }, [tableState?.rows, rows, columns]);

  return (
    <ErrorHandler location="src/components/Table-query/Table.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          {!isNil(header) ? (
            header
          ) : (
            <React.Fragment>
              {theConfiguration?.showHeader === true && (
                <Header
                  {...{
                    currentPage: currentPage + 1,
                    headerBottom,
                    headerLeft,
                    headerRight,
                    headerTop,
                    pageSizeOptions: theConfiguration?.pageSizeOptions,
                    pagination: paginationProps,
                    resultsPerPage,
                    setResultsPerPage,
                    showCompactPagination: theConfiguration?.showCompactPagination,
                    showGoToPage: theConfiguration?.showGoToPage,
                    showNumberedPagination: theConfiguration?.showNumberedPagination,
                    showResultsCount: theConfiguration?.showResultsCount,
                    showResultsPerPage: theConfiguration?.showResultsPerPage,
                    totalResults: totalRecords,
                  }}
                />
              )}
            </React.Fragment>
          )}
          <DataTable className={isLoading ? 'isLoading' : ''} hideTitleHeader tableState={tableState} title="" />
          {!isNil(footer) ? (
            footer
          ) : (
            <React.Fragment>
              {theConfiguration?.showFooter === true && (
                <Footer
                  {...{
                    footerBottom,
                    footerLeft,
                    footerRight,
                    footerTop,
                    pagination: paginationProps,
                    resultsPerPage,
                    showNumberedPagination: theConfiguration?.showNumberedPagination,
                    showResultsCount: theConfiguration?.showResultsCount,
                    totalResults: totalRecords,
                  }}
                />
              )}
            </React.Fragment>
          )}
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Cell: PropTypes.func,
      Filter: PropTypes.func,
      Header: PropTypes.string,
      sortable: PropTypes.bool,
      width: PropTypes.number,
    })
  ),
  configuration: PropTypes.shape({
    pageSizeDefault: PropTypes.number,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    showCompactPagination: PropTypes.bool,
    showFooter: PropTypes.bool,
    showGoToPage: PropTypes.bool,
    showHeader: PropTypes.bool,
    showNumberedPagination: PropTypes.bool,
    showResultsCount: PropTypes.bool,
    showResultsPerPage: PropTypes.bool,
  }),
  error: PropTypes.shape({
    request: PropTypes.shape({
      status: PropTypes.number,
      statusText: PropTypes.string,
    }),
  }),
  footer: PropTypes.element,
  footerBottom: PropTypes.element,
  footerLeft: PropTypes.element,
  footerRight: PropTypes.element,
  footerTop: PropTypes.element,
  header: PropTypes.element,
  headerBottom: PropTypes.element,
  headerLeft: PropTypes.element,
  headerRight: PropTypes.element,
  headerTop: PropTypes.element,
  isLoading: PropTypes.bool,
  noDataMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  requestArgs: PropTypes.shape({
    page: PropTypes.number,
    size: PropTypes.number,
    sort: PropTypes.string,
  }),
  requestFunction: PropTypes.func,
  requestKey: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })),
  totalPages: PropTypes.number,
  totalRecords: PropTypes.number,
};

Table.defaultProps = {
  columns: [],
  configuration: {},
  error: null,
  footer: null,
  footerBottom: null,
  footerLeft: null,
  footerRight: null,
  footerTop: null,
  header: null,
  headerBottom: null,
  headerLeft: null,
  headerRight: null,
  headerTop: null,
  isLoading: false,
  noDataMessage: null,
  requestArgs: {},
  requestFunction: null,
  requestKey: 'table',
  rows: [],
  totalPages: 0,
  totalRecords: 0,
};
